import React, { PureComponent } from 'react';
import { browserHistory } from 'react-router';

import { gettext } from '@eventbrite/i18n';
import flowRight from 'lodash/flowRight';
import * as analytics from '@eventbrite/site-analytics';
import { joinPath } from '@eventbrite/path-utils';

import { withMainControls } from '@eventbrite/eds-structure';
import { withOverlayControls } from '@eventbrite/eds-structure';
import { SelectField } from '@eventbrite/eds-input-field';

import {
    COUNTRIES_FOR_SELECT_FIELD,
    FINANCIAL_RELIEF_PATH,
    US_STATES_FOR_SELECT_FIELD,
} from '../constants';

import ArgentinaInfo from '../components/countries/ArgentinaInfo';
import AustraliaInfo from '../components/countries/AustraliaInfo';
import AustriaInfo from '../components/countries/AustriaInfo';
import BelgiumInfo from '../components/countries/BelgiumInfo';
import BrazilInfo from '../components/countries/BrazilInfo';
import CanadaInfo from '../components/countries/CanadaInfo';
import ChinaInfo from '../components/countries/ChinaInfo';
import DenmarkInfo from '../components/countries/DenmarkInfo';
import FranceInfo from '../components/countries/FranceInfo';
import GermanyInfo from '../components/countries/GermanyInfo';
import HongKongInfo from '../components/countries/HongKongInfo';
import IndiaInfo from '../components/countries/IndiaInfo';
import IrelandInfo from '../components/countries/IrelandInfo';
import ItalyInfo from '../components/countries/ItalyInfo';
import JapanInfo from '../components/countries/JapanInfo';
import NetherlandsInfo from '../components/countries/NetherlandsInfo';
import NewZealandInfo from '../components/countries/NewZealandInfo';
import NorwayInfo from '../components/countries/NorwayInfo';
import SaudiArabiaInfo from '../components/countries/SaudiArabiaInfo';
import ScotlandInfo from '../components/countries/ScotlandInfo';
import SingaporeInfo from '../components/countries/SingaporeInfo';
import SouthKoreaInfo from '../components/countries/SouthKoreaInfo';
import SpainInfo from '../components/countries/SpainInfo';
import SwedenInfo from '../components/countries/SwedenInfo';
import SwitzerlandInfo from '../components/countries/SwitzerlandInfo';
import ThailandInfo from '../components/countries/ThailandInfo';
import UnitedKingdomInfo from '../components/countries/UnitedKingdomInfo';
import UnitedStatesInfo from '../components/countries/UnitedStatesInfo';
import WalesInfo from '../components/countries/WalesInfo';

import './Covid19Page.scss';

const COUNTRY_MAP = {
    argentina: ArgentinaInfo,
    australia: AustraliaInfo,
    austria: AustriaInfo,
    belgium: BelgiumInfo,
    brazil: BrazilInfo,
    canada: CanadaInfo,
    china: ChinaInfo,
    denmark: DenmarkInfo,
    france: FranceInfo,
    germany: GermanyInfo,
    'hong-kong': HongKongInfo,
    india: IndiaInfo,
    ireland: IrelandInfo,
    italy: ItalyInfo,
    japan: JapanInfo,
    netherlands: NetherlandsInfo,
    'new-zealand': NewZealandInfo,
    norway: NorwayInfo,
    'saudi-arabia': SaudiArabiaInfo,
    scotland: ScotlandInfo,
    singapore: SingaporeInfo,
    'south-korea': SouthKoreaInfo,
    spain: SpainInfo,
    sweden: SwedenInfo,
    switzerland: SwitzerlandInfo,
    thailand: ThailandInfo,
    'united-kingdom': UnitedKingdomInfo,
    'united-states': UnitedStatesInfo,
    wales: WalesInfo,
};

const countryUrl = (country) => FINANCIAL_RELIEF_PATH + '/' + country + '/';

const covidCountryListForBots = () =>
    COUNTRIES_FOR_SELECT_FIELD.map((element) => (
        <a
            className="eds-l-pad-right-2"
            key={element.value}
            href={countryUrl(element.value)}
        >
            {element.display}&nbsp;
        </a>
    ));

const covidInfoAndCountrySelector = (
    countrySelected,
    defaultCountrySelected,
    handleCountrySelect,
    isBot,
) => (
    <div>
        <p className="eds-text-bl">
            {gettext(
                'The novel coronavirus, COVID-19, is disrupting the live event industry world wide. And until live experiences are back on track, economic assistance could help ensure our industry remains strong.',
            )}
        </p>
        <br />
        <p>
            {gettext(
                'It’s because of this we want event creators, promoters, and venue owners across the globe to know about the options available in your part of the world.',
            )}
        </p>
        <br />
        <p className="eds-l-mar-bot-4">
            {gettext(
                'Choose your country to find out more about financial relief available:',
            )}
        </p>
        <div className="covid-selector">
            {!isBot && (
                <SelectField
                    label={gettext('Country')}
                    placeholder={gettext('Select a country')}
                    values={COUNTRIES_FOR_SELECT_FIELD}
                    onChange={handleCountrySelect}
                    defaultValue={countrySelected || defaultCountrySelected}
                />
            )}
            {isBot && covidCountryListForBots()}
        </div>
    </div>
);

class Covid19Page extends PureComponent {
    state = {
        countrySelected: '',
    };

    _handleCountrySelect = (countrySelected) => {
        this.setState({
            countrySelected,
        });

        this._setDocumentTitle(countrySelected);
        browserHistory.push(
            FINANCIAL_RELIEF_PATH + '/' + countrySelected + '/',
        );
    };

    _setDocumentTitle = (countrySelected, regionSelected) => {
        let titleName = '';

        if (
            countrySelected &&
            countrySelected === 'united-states' &&
            regionSelected
        ) {
            titleName = US_STATES_FOR_SELECT_FIELD.find(
                (element) => element.value === regionSelected,
            ).display;
        } else if (countrySelected) {
            const country = COUNTRIES_FOR_SELECT_FIELD.find(
                (element) => element.value === countrySelected,
            );

            if (country) {
                titleName = country.display;
            }
        }

        document.title = gettext(
            'COVID-19: %(titleName)s Financial relief and loan information | Eventbrite',
            { titleName: titleName },
        );
    };

    componentDidMount() {
        const { defaultCountrySelected, defaultRegionSelected } = this.props;

        this.setState({
            countrySelected: defaultCountrySelected,
        });

        this._setDocumentTitle(defaultCountrySelected, defaultRegionSelected);
    }

    render() {
        const { defaultCountrySelected, defaultRegionSelected, request } =
            this.props;
        const { countrySelected } = this.state;

        const isBot = request.is_bot;
        const CountryInfo = COUNTRY_MAP[countrySelected] || UnitedStatesInfo;

        const basicInfoAndCountrySelector = covidInfoAndCountrySelector(
            countrySelected,
            defaultCountrySelected,
            this._handleCountrySelect,
            isBot,
        );

        return (
            <div className="covid-hub">
                <div>
                    <div className="covid-bg-image-wrapper eds-hide-sn eds-hide-sm eds-hide-sw">
                        <div className="covid-bg-image"></div>
                    </div>
                    <div className="covid-header eds-l-pad-hor-12 eds-align--center eds-hide-sn eds-hide-sm eds-hide-sw">
                        <div className="covid-header__left-content eds-l-sm-pad-right-0 eds-l-pad-right-12">
                            <h1 className="eds-text-hl eds-text-color--background eds-l-mar-bot-8">
                                {gettext(
                                    'Coronavirus (COVID-19) Financial Relief and Loan Information',
                                )}
                            </h1>
                            <h2 className="eds-text-hs eds-text-color--background">
                                {gettext('Resources for Event Organizers')}
                            </h2>
                        </div>
                        <div className="covid-header__right-content">
                            <div className="covid-header__right-content_inner eds-l-mar-hor-10 eds-l-mar-vert-12">
                                {basicInfoAndCountrySelector}
                            </div>
                        </div>
                    </div>
                    <div className="eds-l-mar-hor-10 eds-l-mar-top-12 eds-l-mar-bot-6 eds-hide-mn eds-hide-md eds-hide-mw eds-hide-ln eds-hide-lg eds-hide-lw">
                        <h1 className="eds-text-hl eds-l-mar-bot-8">
                            {gettext(
                                'Coronavirus (COVID-19) Financial Relief and Loan Information',
                            )}
                        </h1>
                        <h2 className="eds-text-hs eds-l-mar-bot-4">
                            {gettext('Resources for Event Organizers')}
                        </h2>
                        {basicInfoAndCountrySelector}
                    </div>
                    {countrySelected && (
                        <div className="covid-content eds-g-group eds-l-pad-hor-12 eds-l-mar-vert-12 eds-align--center">
                            <CountryInfo
                                defaultRegionSelected={defaultRegionSelected}
                            />
                        </div>
                    )}
                </div>
            </div>
        );
    }
}

export default flowRight(withOverlayControls, withMainControls)(Covid19Page);
